import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { CanActivateService } from '../../../Services/CanActivate/can-activate.service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { BranchSelectionService } from '../../../Services/branch-selection.service';
import { ErrorMessageDialogComponent } from '../../../Shared Components/error-message-dialog/error-message-dialog.component';
import { APILandingPageTable } from '../../../interfaces/APILandingPageTable.interface';
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { DDIGCompanySearchOnly } from '../../../interfaces/DDIGCompanySearchOnly.interface';
import { UserProfileData } from '../../../interfaces/user-profile-data';
import { APIDetailsDialogComponentComponent } from '../apidetails-dialog-component/apidetails-dialog-component.component';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AccountsServiceService } from '../../../Services/accounts-service.service';

const ELEMENT_DATA: APILandingPageTable[] = [];

/* istanbul ignore next */
export class NoResultsErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
    selector: 'app-api-landing-page',
    templateUrl: './api-landing-page.component.html',
    styleUrls: ['./api-landing-page.component.scss'],
    animations: [
        trigger('slideDownInOut', [
            state('void', style({
                opacity: 0,
                transform: 'translateY(-10%)',
            })),
            transition('void => *', animate('600ms ease-out')),
            transition('* => void', animate('600ms ease-in')),
        ]),
    ],
    standalone: false
})
export class APILandingPageComponent implements OnInit {
  companyName$: Observable<string | undefined>;
  displayedColumns: string[] = [
    'integrationName',
    'productType',
    'totalActiveCredentials',
    'billingAnniversaryDate',
    'moreDetails',
  ];
  dataSource = new MatTableDataSource(ELEMENT_DATA);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  selectedIntegrationName: String = '';
  private destroy$ = new Subject<void>();
  companyId: string | null = null;
  userProfileData: UserProfileData | undefined;
  options: DDIGCompanySearchOnly[] = [];
  filteredOptions: Observable<DDIGCompanySearchOnly[]> | undefined;
  form1!: UntypedFormGroup;
  noResultsFound: boolean = false;
  errorStateMatcher: ErrorStateMatcher = new NoResultsErrorStateMatcher();
  private companyNameSubject = new BehaviorSubject<string | undefined>(
    undefined
  );
  selectedBranchId: string | null = null;
  selectedAccountName: string | undefined;
  selectedAccountNumber: string | undefined;

  constructor(
    public canActivateService: CanActivateService,
    public backend: BackendAccessService,
    public router: Router,
    public datePipe: DatePipe,
    public dialog: MatDialog,
    private branchSelectionService: BranchSelectionService,
    private loadingService: LoadingService,
    public formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private accountsService: AccountsServiceService
  ) {
    this.companyName$ = this.canActivateService.companyProfile$.pipe(
      map((companyProfile) => companyProfile?.companyName)
    );
    this.companyName$ = this.companyNameSubject.asObservable();
  }
  /* istanbul ignore next */
  ngOnInit(): void {
    combineLatest([
      this.canActivateService.companyProfile$.pipe(distinctUntilChanged()),
      this.canActivateService.multipleBranches$.pipe(distinctUntilChanged()),
      this.branchSelectionService.selectedBranch$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(([companyProfile, hasMultipleBranches, selectedBranchId]) => {
          if (companyProfile) {
            this.companyId = companyProfile.companyAccountId;
            this.branchSelectionService.setCompanyName(
              companyProfile.companyName
            );
          }

          if (!hasMultipleBranches) {
            // Single branch scenario
            console.log('Single branch scenario - companyId:', this.companyId);
            this.selectedBranchId = this.companyId!;
            this.branchSelectionService.setSelectedBranch(this.companyId!);
          } else {
            // Multiple branches scenario
            console.log('Multiple branches scenario');
            this.selectedBranchId = selectedBranchId;
          }
        }),
        filter(
          ([_, hasMultipleBranches, selectedBranchId]) =>
            !hasMultipleBranches || !!selectedBranchId
        ),
        switchMap(([_, hasMultipleBranches, selectedBranchId]) => {
          const branchId = hasMultipleBranches
            ? selectedBranchId
            : this.companyId;
          return this.loadAPISummaryData(branchId);
        }),
        catchError((error) => {
          console.error('Error during data loading:', error);
          return of([]);
        }),
        finalize(() => this.loadingService.hide())
      )
      .subscribe();

    // Initialize form
    this.form1 = this.formBuilder.group({
      myControl: new UntypedFormControl('', [Validators.required]),
    });

    this.filteredOptions = this.form1.controls['myControl'].valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value.businessId)),
      switchMap((businessId) =>
        businessId
          ? this.loadCompaniesTechSupport(businessId)
          : of(this.options.slice())
      )
    );
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }
  /* istanbul ignore next */
  loadAPISummaryData(selectedBranch?: string | null): Observable<any> {
    this.loadingService.show();
    return this.backend.getAPICredentialOverviewData(selectedBranch!).pipe(
      tap((data) => this.processData(data)),
      catchError((error) => {
        console.error('Error fetching API credential overview data:', error);
        return of([]);
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /* istanbul ignore next */
  loadAPISummaryTechSupport(event: MatAutocompleteSelectedEvent): void {
    // Extract the branchId (assuming accountId is used as branchId) from the selected option
    const branchId = event.option.value.accountId;
    this.companyNameSubject.next(event.option.value.businessId);
    this.selectedBranchId = branchId;
    this.selectedAccountName = event.option.value.businessId;
    this.selectedAccountNumber = event.option.value.accountNumber;

    this.backend
      .getAPICredentialOverviewData(branchId)
      .pipe(
        catchError((error) => {
          console.error('Error fetching API credential overview data:', error);
          return of([]);
        })
      )
      .subscribe({
        next: (data) => {
          this.dataSource.data = data;
          if (data.length === 0) {
            this.openNoBranchConfiguredDialog();
          }
        },
        error: (error) => {
          // Handle any errors here
          console.error('Failed to load API Credential Overview Data:', error);
        },
      });
  }
  /* istanbul ignore next */
  private processData(data: any): void {
    // Assuming `data` is directly the array of integration objects
    if (Array.isArray(data)) {
      this.dataSource.data = data;
      if (data.length === 0) {
        this.openNoBranchConfiguredDialog();
      }
    } else {
      console.error('Invalid data received:', data);
      // Handle the case where data is not an array as expected
    }
    this.cdr.detectChanges();
  }
  /* istanbul ignore next */
  loadCompaniesTechSupport(value: string): Observable<DDIGCompanySearchOnly[]> {
    if (!value) {
      return of([]); // Don't call the API if the input is empty
    }
    this.loadingService.show();
    return this.backend.searchAccountsAPICreds(value).pipe(
      tap((results: DDIGCompanySearchOnly[]) => {
        this.options = results;
        this.accountsService.DDIGaccounts.next(results);
        this.noResultsFound = results.length === 0;
        this.form1.controls['myControl'].setErrors(
          this.noResultsFound ? { noResults: true } : null
        );
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of([]); // Return an empty observable in case of an error
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /* istanbul ignore next */
  displayNameAPI(subject: any): string {
    if (!subject) {
      return '';
    }
    if (subject.businessId) {
      return (
        subject.businessId +
        ' (Account #: ' +
        (subject.accountNumber ? subject.accountNumber : 'N/A') +
        ')' +
        '\n' +
        ' ' +
        subject.addressCity +
        ', ' +
        subject.addressState
      );
    } else if (subject.accountName) {
      return subject.accountName;
    } else {
      return ''; // Return an empty string if no conditions match
    }
  }
  /* istanbul ignore next */
  openAPIDetailsDialog(element: any): void {
    console.log(
      'Opening details dialog with selectedBranchId:',
      this.selectedBranchId
    );
    if (!this.selectedBranchId) {
      console.error('No selected branch ID available.');
      return;
    }
    const dialogRef = this.dialog.open(APIDetailsDialogComponentComponent, {
      width: '80%',
      data: {
        integrationId: element.integrationId,
        integrationName: element.integrationName,
        productType: element.productType,
        skuCode: element.skuCode,
        totalActiveCredentials: element.totalActiveCredentials,
        branchId: this.selectedBranchId,
      },
    });
  }

  /* istanbul ignore next */
  openNoBranchConfiguredDialog(): void {
    const dialogRef = this.dialog.open(ErrorMessageDialogComponent, {
      width: '400px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed');
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
